import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/ui/Layout';
import SessionExpiredModal from './components/ui/SessionExpiredModal';
import { api } from './lib/api';
import { captureHandoffCode } from './lib/acquisition-handoff';
import { SessionProvider } from './lib/session';
import { PublishProvider } from './lib/usePublish';
import type { SessionUser, SignupGateState } from './lib/types';

const HomeScreen = lazy(() => import('./pages/home/HomeScreen'));
const EditorPage = lazy(() => import('./pages/editor/EditorPage'));
const HeaderEditorPage = lazy(() => import('./pages/editor/HeaderEditorPage'));
const FooterEditorPage = lazy(() => import('./pages/editor/FooterEditorPage'));
const CollectionListPage = lazy(() => import('./pages/collections/CollectionListPage'));
const MediaPage = lazy(() => import('./pages/media/MediaPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const SitePage = lazy(() => import('./pages/site/SitePage'));
const ContactsPage = lazy(() => import('./pages/contacts/ContactsPage'));
const OperationsPage = lazy(() => import('./pages/contacts/OperationsPage'));
const ContactDetailPage = lazy(() => import('./pages/contacts/ContactDetailPage'));
const ContactsImportPage = lazy(() => import('./pages/contacts/ContactsImportPage'));
const SubmissionsPage = lazy(() => import('./pages/submissions/SubmissionsPage'));
const ServiceStepsPage = lazy(() => import('./pages/contacts/ServiceStepsPage'));
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'));
const BookingsPage = lazy(() => import('./pages/bookings/BookingsPage'));
const ReputationPage = lazy(() => import('./pages/reputation/ReputationPage'));
const SurveysPage = lazy(() => import('./pages/surveys/SurveysPage'));
const SurveyEditorPage = lazy(() => import('./pages/surveys/SurveyEditorPage'));
const VisibilityPage = lazy(() => import('./pages/visibility/VisibilityPage'));
const ChatbotPage = lazy(() => import('./pages/chatbot/ChatbotPage'));
const AccountPage = lazy(() => import('./pages/account/AccountPage'));
const HelpPage = lazy(() => import('./pages/help/HelpPage'));
const AccountActionPage = lazy(() => import('./pages/account/AccountActionPage'));
const SetupPage = lazy(() => import('./pages/SetupPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const OnboardingWizard = lazy(() => import('./pages/onboarding/OnboardingWizard'));
const GettingStartedPage = lazy(() => import('./pages/getting-started/GettingStartedPage'));
const PaywallScreen = lazy(() => import('./pages/billing/PaywallScreen'));
const TermsGateScreen = lazy(() => import('./pages/legal/TermsGateScreen'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const MyBillingPage = lazy(() => import('./pages/billing/MyBillingPage'));
const BillingSettingsPage = lazy(() => import('./pages/platform/BillingSettingsPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const PluginsPage = lazy(() => import('./pages/platform/PluginsPage'));
const DatasetsPage = lazy(() => import('./pages/platform/DatasetsPage'));
const PropertyComparablesPage = lazy(() => import('./pages/PropertyComparablesPage'));
const PropertyMarketReferencesPage = lazy(() => import('./pages/PropertyMarketReferencesPage'));
const FiscalSettingsPage = lazy(() => import('./pages/platform/FiscalSettingsPage'));
const InfrastructurePage = lazy(() => import('./pages/platform/InfrastructurePage'));
const CostsPage = lazy(() => import('./pages/platform/CostsPage'));

const routeFallback = (
  <div className="min-h-screen bg-ui-bg flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
  </div>
);

type AppState = 'loading' | 'needs-setup' | 'needs-login' | 'needs-signup' | 'needs-terms' | 'onboarding' | 'needs-payment' | 'ready';

// El registro no debe abrirse por un fallo del endpoint público de configuración
// ni por una versión antigua del Worker que todavía no devuelva signupGate.
const CLOSED_SIGNUP_GATE_FALLBACK: SignupGateState = {
  mode: 'closed',
  waitlistTitle: 'Lista de espera',
  waitlistMessage: 'Déjanos tus datos y te avisaremos cuando abramos nuevos lugares.',
  waitlistUrl: null,
  waitlistCampaignSlug: null,
  openedAt: null,
  waitlistPurgedCount: 0,
};

function preferredLoggedOutState(signupGate?: SignupGateState): AppState {
  if (typeof window === 'undefined') return 'needs-login';
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  // El gate remoto manda también sobre el deep link público. Si el registro está
  // cerrado, no debemos enseñar la pantalla de alta aunque alguien conserve una
  // URL antigua o llegue directamente a /signup.
  if (path === '/signup' && (signupGate?.mode === 'open' || signupGate?.mode === 'allowlist')) {
    return 'needs-signup';
  }
  return 'needs-login';
}

function wantsOnboarding(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('onboarding') === '1';
}

function clearSaasOnboardingIntent(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('onboarding');
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', next);
}

export default function App() {
  return (
    <PublishProvider>
      <AppContent />
    </PublishProvider>
  );
}

function AppContent() {
  // ACQ-00 — si la persona llega desde una herramienta pública, el código viene en
  // el fragmento. Se guarda ANTES de cualquier navegación del panel (alta,
  // confirmación de correo) y se canjea ya con sesión, dentro del onboarding.
  if (typeof window !== 'undefined') captureHandoffCode();

  const [state, setState] = useState<AppState>('loading');
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
  // Interruptor temporal del login por enlace mágico (se lee en el arranque vía /api/setup).
  const [magicLinkEnabled, setMagicLinkEnabled] = useState(false);
  const [signupGate, setSignupGate] = useState<SignupGateState>(CLOSED_SIGNUP_GATE_FALLBACK);

  const isAccountAction = typeof window !== 'undefined' && window.location.pathname === '/account-action';

  // Resuelve a qué pantalla mandar al usuario: setup global → auth → wizard → panel.
  const refresh = useCallback(async () => {
    try {
      const { needsSetup, magicLinkEnabled: magicEnabled, signupGate: nextSignupGate } = await api.setup.check();
      setMagicLinkEnabled(!!magicEnabled);
      const effectiveSignupGate = nextSignupGate ?? CLOSED_SIGNUP_GATE_FALLBACK;
      setSignupGate(effectiveSignupGate);
      if (needsSetup) { setState('needs-setup'); return; }
      const me = await api.auth.me();
      if (!me.user) { setSessionUser(null); setState(preferredLoggedOutState(effectiveSignupGate)); return; }
      setSessionUser(me.user);

      // Se publicó una versión nueva de Términos/Privacidad y este usuario (de
      // tenant, no saas_admin) todavía no la acepta — gate duro antes de cualquier
      // otra cosa (onboarding, pago, panel).
      if (me.pendingLegalAcceptance) { setState('needs-terms'); return; }

      // El dueño del SaaS (saas_admin) nunca pasa por el muro de pago.
      if (me.user.role === 'saas_admin') {
        if (me.needsOnboarding && wantsOnboarding()) {
          setState('onboarding');
          return;
        }
        if (!me.needsOnboarding) clearSaasOnboardingIntent();
        setState('ready');
        return;
      }

      if (me.needsOnboarding) {
        setState(me.onboardingPaused && !wantsOnboarding() ? 'ready' : 'onboarding');
        return;
      }

      setState('ready');
    } catch {
      // Si no podemos leer la política remota, no abrir accidentalmente el alta
      // por el fallback local (cuyo valor histórico es `open`).
      setSignupGate(CLOSED_SIGNUP_GATE_FALLBACK);
      setState('needs-login');
    }
  }, []);

  useEffect(() => { void Promise.resolve().then(() => refresh()); }, [refresh]);

  if (isAccountAction) {
    return <Suspense fallback={routeFallback}><AccountActionPage /></Suspense>;
  }

  // Página pública de Términos/Privacidad — accesible con o sin sesión, enlazada
  // desde el signup y desde el gate de re-aceptación.
  if (typeof window !== 'undefined' && window.location.pathname.replace(/\/+$/, '') === '/legal') {
    return (
      <Suspense fallback={routeFallback}>
        <LegalPage />
      </Suspense>
    );
  }

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-ui-bg flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state === 'needs-setup') {
    return (
      <Suspense fallback={routeFallback}>
        <SetupPage onComplete={() => setState('needs-login')} />
      </Suspense>
    );
  }

  if (state === 'needs-login') {
    return (
      <Suspense fallback={routeFallback}>
        <LoginPage onLogin={refresh} onSwitchToSignup={() => setState('needs-signup')} magicLinkEnabled={magicLinkEnabled} signupGate={signupGate} />
      </Suspense>
    );
  }

  if (state === 'needs-signup') {
    // GOL-07: el alta ya no deja sesión iniciada — el usuario confirma su correo
    // (enlace de activación) y desde ahí entra por needs-login/needs-onboarding.
    return (
      <Suspense fallback={routeFallback}>
        <SignupPage onSwitchToLogin={() => setState('needs-login')} signupGate={signupGate} />
      </Suspense>
    );
  }

  if (state === 'needs-terms') {
    return (
      <Suspense fallback={routeFallback}>
        <TermsGateScreen onResolved={refresh} onLogout={() => setState('needs-login')} />
      </Suspense>
    );
  }

  if (state === 'needs-payment') {
    // Tras elegir una opción, entramos al panel. La elección de un plan nunca es un muro.
    return (
      <Suspense fallback={routeFallback}>
        <PaywallScreen onResolved={refresh} onLogout={() => setState('needs-login')} />
      </Suspense>
    );
  }

  if (state === 'onboarding') {
    return (
      <Suspense fallback={routeFallback}>
        <OnboardingWizard
          canSeePlatformMetrics={sessionUser?.role === 'saas_admin'}
          onComplete={() => {
            clearSaasOnboardingIntent();
            setState(sessionUser?.role === 'saas_admin' ? 'ready' : 'needs-payment');
          }}
          onExit={() => {
            clearSaasOnboardingIntent();
            setState('ready');
          }}
        />
      </Suspense>
    );
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <SessionProvider initialUser={sessionUser}>
        <SessionExpiredModal />
        <Suspense fallback={routeFallback}>
          <Routes>
              <Route path="/editor/header" element={<HeaderEditorPage />} />
              <Route path="/editor/footer" element={<FooterEditorPage />} />
              <Route path="/editor/:pageId" element={<EditorPage />} />
              <Route element={<Layout />}>
                <Route index element={<HomeScreen />} />
                <Route path="pages/*" element={<Navigate to="/site?tab=paginas" replace />} />
                {/* Motor de colecciones genéricas — reemplaza a las rutas typed properties/agents/posts */}
                <Route path="getting-started" element={<GettingStartedPage />} />
                <Route path="c/:key" element={<CollectionListPage />} />
                <Route path="media" element={<MediaPage />} />
                {/* CLI-24 — Clientes: dos vistas hermanas con URL propia (Personas/Operaciones) */}
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="contacts/operations" element={<OperationsPage />} />
                <Route path="contacts/import" element={<ContactsImportPage />} />
                <Route path="contacts/steps" element={<ServiceStepsPage />} />
                <Route path="contacts/steps/:templateId" element={<ServiceStepsPage />} />
                <Route path="contacts/:contactId" element={<ContactDetailPage />} />
                <Route path="submissions" element={<SubmissionsPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="tools" element={<ToolsPage />} />
                <Route path="tools/comparables" element={<PropertyComparablesPage />} />
                <Route path="tools/market-references" element={<PropertyMarketReferencesPage />} />
                <Route path="bookings" element={<BookingsPage />} />
                <Route path="reputation" element={<ReputationPage />} />
                <Route path="surveys" element={<SurveysPage />} />
                <Route path="surveys/:id" element={<SurveyEditorPage />} />
                <Route path="visibility" element={<VisibilityPage />} />
                <Route path="chatbot" element={<ChatbotPage />} />
                <Route path="qr" element={<Navigate to="/media?tab=qr" replace />} />
                <Route path="account" element={<AccountPage />} />
                <Route path="ayuda" element={<HelpPage />} />
                <Route path="billing" element={<MyBillingPage />} />
                <Route path="platform/infrastructure" element={<InfrastructurePage />} />
                <Route path="platform/billing" element={<BillingSettingsPage />} />
                <Route path="platform/plugins" element={<PluginsPage />} />
                <Route path="platform/datasets" element={<DatasetsPage />} />
                <Route path="platform/fiscal" element={<FiscalSettingsPage />} />
                <Route path="platform/costs" element={<CostsPage />} />
                <Route path="site/*" element={<SitePage />} />
                <Route path="seo/*" element={<Navigate to="/visibility?tab=local" replace />} />
                <Route path="settings/*" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
          </Routes>
        </Suspense>
      </SessionProvider>
    </BrowserRouter>
  );
}
